// eslint-disable-next-line no-unused-vars
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Logo from './Logo';
import ChatWidget from './ChatWidget';
import { CONTACT } from '../mock';
import useSeo from '../hooks/useSeo';

const LAST_UPDATED = 'February 2026';
const COMPANY = 'Absolute Marine Equipment';
const LEGAL_ENTITY = 'Group Absolute Builders LLC';
const MAILING_ADDRESS = '2670 Colonial Blvd #2067, Fort Myers, FL 33907';
const STATE = 'Florida';

export default function PrivacyPolicy() {
  useSeo({
    title: `Privacy Policy — ${COMPANY}`,
    description:
      'Privacy policy for Absolute Marine Equipment (Group Absolute Builders LLC). How we collect, use, and protect visitor and customer information — plus your rights and choices.',
    canonical: 'https://absolutebarges.com/privacy',
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="App bg-black min-h-screen text-zinc-200">
      <ChatWidget />
      {/* Slim header (independent of the marketing site nav) */}
      <header className="border-b border-white/5 bg-black/90 backdrop-blur">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
          <Logo />
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs sm:text-sm text-zinc-400 hover:text-cyan-300 transition-colors"
            data-testid="privacy-back-home-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to site
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="mb-10 sm:mb-14">
          <div className="text-[11px] sm:text-xs uppercase tracking-[0.28em] text-cyan-300 font-semibold">
            Legal
          </div>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-white">
            Privacy Policy
          </h1>
          <p className="mt-3 text-sm text-zinc-500">
            Last updated: {LAST_UPDATED}
          </p>
        </div>

        <div className="space-y-10 leading-relaxed text-[15px] sm:text-base text-zinc-300">
          <section>
            <p>
              {COMPANY} (a d/b/a of {LEGAL_ENTITY}) (“
              <strong className="text-white">we</strong>,” “
              <strong className="text-white">us</strong>,” or “
              <strong className="text-white">our</strong>”) builds heavy-duty
              equipment for marine contractors — HDPE barges, pneumatic pile
              driving hammers, and related tooling. This Privacy Policy
              explains what information we collect from visitors and customers,
              how we use it, and the choices you have. We keep it short and
              plain because you have work to do.
            </p>
          </section>

          <Section title="1. Information we collect">
            <p>
              We collect information you voluntarily provide when you contact
              us for a quote, order equipment, request delivery, or
              communicate with our team. That typically includes:
            </p>
            <ul className="mt-3 space-y-1.5 pl-5 list-disc marker:text-cyan-400/60">
              <li>Name and business (or company name)</li>
              <li>Phone number and email address</li>
              <li>
                Project details you share with us (jobsite location, desired
                barge configuration, hammer requirements, timeline, delivery
                needs, etc.)
              </li>
              <li>
                Payment and financing-related information supplied during an
                order (handled through our payment or financing partners — we
                do not store card numbers on our systems)
              </li>
              <li>
                Any correspondence you send us by email, phone, text, or the
                contact form
              </li>
            </ul>
            <p className="mt-3">
              We may also automatically collect limited technical information
              when you visit our website — such as browser type, device type,
              approximate location derived from IP address, and pages viewed —
              via our web hosting provider’s standard logs. This information
              is used to keep the site running, prevent abuse, and improve the
              user experience.
            </p>
          </Section>

          <Section title="2. How we use your information">
            <p>We use your information only to run our business, specifically to:</p>
            <ul className="mt-3 space-y-1.5 pl-5 list-disc marker:text-cyan-400/60">
              <li>
                Respond to your quote request, discuss specs, and prepare a
                written quote
              </li>
              <li>Fulfill orders, coordinate manufacturing, and arrange delivery</li>
              <li>
                Provide warranty service, technical support, and follow-up on
                delivered equipment
              </li>
              <li>Send transactional confirmations, invoices, and delivery updates</li>
              <li>
                Occasionally follow up with prior customers about product
                improvements, new offerings, or check-ins on delivered
                equipment (you can opt out at any time — see below)
              </li>
              <li>
                Comply with legal, tax, and regulatory obligations and, when
                required, respond to lawful requests from public authorities
              </li>
            </ul>
            <p className="mt-3">
              We do <strong className="text-white">not</strong> sell your
              personal information. We do not rent it, trade it, or hand it
              over to marketers, list brokers, or data aggregators.
            </p>
          </Section>

          <Section title="3. Who we share your information with">
            <p>
              We share your information only with the small number of service
              providers we rely on to run our business, and only for the
              purpose of providing services to you:
            </p>
            <ul className="mt-3 space-y-1.5 pl-5 list-disc marker:text-cyan-400/60">
              <li>
                <strong className="text-white">Email delivery</strong> —
                inquiries submitted through our contact form are routed to us
                using an email-delivery provider (currently EmailJS), which
                also sends you a confirmation email
              </li>
              <li>
                <strong className="text-white">Payment &amp; financing</strong>{' '}
                — if you pay by card or apply for third-party financing, that
                information is handled directly by the applicable processor
                or lender under their own privacy policies
              </li>
              <li>
                <strong className="text-white">Logistics &amp; delivery</strong>{' '}
                — carriers and haulers used to deliver equipment to your job
                site
              </li>
              <li>
                <strong className="text-white">Professional advisors</strong>{' '}
                — attorneys, accountants, and insurers, when necessary
              </li>
              <li>
                <strong className="text-white">Legal compliance</strong> — if
                required by law, court order, or to protect our rights,
                property, or safety
              </li>
            </ul>
          </Section>

          <Section title="4. Cookies and analytics">
            <p>
              Our website uses only the essential cookies and technologies
              needed to make the site work. If we add analytics or advertising
              tracking in the future (for example, Google Analytics or Meta
              Pixel), we will update this policy and, where required by law,
              request your consent. You can disable cookies in your browser
              settings — the site will continue to work normally without them.
            </p>
          </Section>

          <Section title="5. How we protect your information">
            <p>
              We use commercially reasonable technical and organizational
              measures to protect the information we hold — including secure
              hosting, encrypted connections (HTTPS), and access limited to
              employees who need it to do their job. No online transmission or
              storage is 100% secure, however, so we cannot guarantee absolute
              security.
            </p>
          </Section>

          <Section title="6. How long we keep your information">
            <p>
              We retain your information for as long as we have a business
              relationship with you, and after that only as long as we need it
              to service your warranty, meet legal or tax obligations, or
              resolve disputes. Quote inquiries that never turn into orders
              are retained for a reasonable period and then deleted or
              archived.
            </p>
          </Section>

          <Section title="7. Your rights and choices">
            <p>
              You can ask us at any time to:
            </p>
            <ul className="mt-3 space-y-1.5 pl-5 list-disc marker:text-cyan-400/60">
              <li>Confirm what information we hold about you</li>
              <li>Correct or update your information</li>
              <li>
                Delete your information, subject to any legal, tax, or
                warranty-service obligations that require us to keep it
              </li>
              <li>
                Stop receiving non-transactional emails or texts from us —
                just reply “stop” or email us and we will remove you promptly
              </li>
            </ul>
            <p className="mt-3">
              To exercise any of these rights, contact us using the details
              in Section 10.
            </p>
          </Section>

          <Section title="8. Children">
            <p>
              Our services and website are directed to businesses and adult
              professionals. We do not knowingly collect information from
              anyone under 18. If you believe a minor has provided us
              information, please contact us and we will delete it.
            </p>
          </Section>

          <Section title="9. Changes to this policy">
            <p>
              We may update this policy from time to time — for example, when
              we add a new service or a new payment or financing partner. The
              “Last updated” date at the top of this page will always show
              when the policy was most recently changed. Material changes
              will be communicated on this page and, where appropriate, by
              email to existing customers.
            </p>
          </Section>

          <Section title="10. Contact us">
            <p>
              Questions about this policy or how we handle your information?
              We’re happy to answer them.
            </p>
            <ul className="mt-3 space-y-1.5 pl-5 list-none">
              <li>
                <span className="text-zinc-500">Email:</span>{' '}
                <a
                  href={`mailto:${CONTACT.email}`}
                  className="text-cyan-300 hover:text-cyan-200"
                  data-testid="privacy-contact-email"
                >
                  {CONTACT.email}
                </a>
              </li>
              <li>
                <span className="text-zinc-500">Phone:</span>{' '}
                <a
                  href={`tel:${CONTACT.salesPhone.tel}`}
                  className="text-cyan-300 hover:text-cyan-200"
                >
                  {CONTACT.salesPhone.number}
                </a>
              </li>
              <li>
                <span className="text-zinc-500">Mail:</span>{' '}
                {LEGAL_ENTITY} d/b/a {COMPANY}, {MAILING_ADDRESS}
              </li>
            </ul>
          </Section>

          <Section title="11. SMS and text messages (A2P / TCPA)">
            <p>
              When you provide your mobile phone number and check the SMS
              consent box on our website (or otherwise expressly consent — by
              text or in writing to a member of our team), you are agreeing
              to receive calls and SMS/MMS text messages from us about your
              inquiry, quote, order, delivery, warranty service, and
              (optionally) occasional new-product announcements. Consent is
              not a condition of purchasing any goods or services from us.
            </p>
            <ul className="mt-3 space-y-1.5 pl-5 list-disc marker:text-cyan-400/60">
              <li>Message frequency varies with the stage of your inquiry or order</li>
              <li>Message and data rates may apply based on your carrier plan</li>
              <li>
                Reply <strong className="text-white">STOP</strong> at any time
                to unsubscribe, or <strong className="text-white">HELP</strong>{' '}
                for help
              </li>
              <li>
                We do not sell, rent, or share your mobile number or SMS
                consent data with third parties or affiliates for their
                marketing or promotional purposes
              </li>
              <li>
                Your SMS opt-out does not affect other communications you have
                requested (email quotes, invoices, delivery updates, etc.)
              </li>
            </ul>
            <p className="mt-3">
              Full program details, opt-in and opt-out procedures, and carrier
              disclaimers are set out in our{' '}
              <Link to="/sms-terms" className="text-cyan-300 hover:text-cyan-200">
                SMS &amp; Messaging Terms
              </Link>
              .
            </p>
          </Section>

          <Section title="12. Governing law">
            <p>
              This policy is governed by the laws of the State of {STATE},
              U.S.A., without regard to conflict-of-law principles.
            </p>
          </Section>
        </div>

        <div className="mt-14 pt-6 border-t border-white/5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-xs text-zinc-500">
            © {new Date().getFullYear()} {COMPANY}. All rights reserved.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs sm:text-sm text-cyan-300 hover:text-cyan-200 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to site
          </Link>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="text-white text-lg sm:text-xl font-semibold mb-3">
        {title}
      </h2>
      <div className="text-zinc-300 text-[15px] sm:text-base leading-relaxed">
        {children}
      </div>
    </section>
  );
}
